import React, { useEffect, useState, useContext, useMemo } from "react";
import {
	Alert,
	Box,
	Button,
	Checkbox,
	FormControl,
	FormControlLabel,
	FormGroup,
	FormHelperText,
	FormLabel,
	Grid,
	IconButton,
	List,
	ListItem,
	ListItemText,
	TextField,
	Typography,
	CircularProgress,
	Switch,
} from "@mui/material";
import { agregarDato, obtenerDatos, auditPost } from "../../api/config";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ModalEdit from "./ModalEditSimulation";
import AddIcon from "@mui/icons-material/Add";
import Swal from "sweetalert2";
import { ModalCreateContract } from "./ModalCreateContract";
import { useNavigate } from "react-router-dom";
import { FormNameDesc } from "./FormNameDesc";
import FormConfig from "../Config/FormConfig";
import { GlobalContext } from "../../GlobalContext";
import { useTranslation } from "react-i18next";
import { validateFieldSite } from "../../Labels/descriptions/LabelsDescriptionsRestrictions/Restrictions";

const FormSimulation = () => {
	const { t } = useTranslation();
	const { getModuleId, site_id } = useContext(GlobalContext);
	const [fechaFin, setFechaFin] = useState("");
	const [fechaInicio, setFechaInicio] = useState("");
	const [fechaError, setFechaError] = useState("");
	const [paso, setPaso] = useState(0);
	const [configArea, setConfigArea] = useState([]);
	const [datosContractArea, setDatosContractArea] = useState({});
	const [editingIndex, setEditingIndex] = useState(null);
	const [modalOpen, setModalOpen] = useState(false);
	const [selectedContrato, setSelectedContrato] = useState(null);
	const [checkedError, setCheckedError] = useState("");
	const [checked, setChecked] = useState("");
	const [openModalCrear, setOpenModalCrear] = useState(false);
	const [formValues, setFormValues] = useState({
		name: "",
		description: "",
	});
	const [formErrors, setFormErrors] = useState({
		name: false,
		description: false,
	});
	const [showAlert, setShowAlert] = useState(false);
	const navigate = useNavigate();
	const [formValuesSite, setFormValuesSite] = useState({});
	const [errors, setErrors] = useState({});
	const [showList, setShowList] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [emailNotifications, setEmailNotifications] = useState(false);

	useEffect(() => {
		const configArea = async () => {
			try {
				if (site_id !== null) {
					const datosConfigArea = await obtenerDatos(`/config/get-config/${site_id}`);
					setConfigArea(datosConfigArea);
				}
			} catch (error) {
				console.error("Error al obtener contratos:", error);
			}
		};
		configArea();
	}, [site_id]);

	useEffect(() => {
		const contractArea = async () => {
			setIsLoading(true); // Iniciar carga
			try {
				if (site_id !== null) {
					const datosContractArea = await obtenerDatos(`/contracts/get/${site_id}`);
					setDatosContractArea(datosContractArea);
					setShowList(true);
				}
			} catch (error) {
				console.error("Error al obtener contratos:", error);
			} finally {
				setIsLoading(false); // Finalizar carga
			}
		};
		contractArea();
	}, [fechaInicio, fechaFin, site_id]);

	const valuesSite = useMemo(() => {
		return {
			[t("lab_tool.site.labels.nombre")]: configArea.nombre,
			[t("lab_tool.site.labels.opmin")]: configArea.opmin,
			[t("lab_tool.site.labels.cu")]: configArea.cu,
			[t("lab_tool.site.labels.co")]: configArea.co,
			[t("lab_tool.site.labels.slmin")]: configArea.slmin,
		};
	}, [configArea, t]);

	const tooltips = [
		t("lab_tool.site.tooltips.nombre"),
		t("lab_tool.site.tooltips.opmin"),
		t("lab_tool.site.tooltips.cu"),
		t("lab_tool.site.tooltips.co"),
		t("lab_tool.site.tooltips.slmin"),
	];
	const siteFieldConfigs = {
		[t("lab_tool.site.labels.nombre")]: {
			minLength: 3,
			regex: /^(?=.*[A-Za-zÁÉÍÓÚáéíóú])[A-Za-zÁÉÍÓÚáéíóú0-9 _-]+$/,
		},
		[t("lab_tool.site.labels.opmin")]: {
			type: "number",
			allowDecimal: false, // enteros
		},
		[t("lab_tool.site.labels.cu")]: {
			type: "number",
			allowDecimal: true, // permite decimales
		},
		[t("lab_tool.site.labels.co")]: {
			type: "number",
			allowDecimal: true,
		},
		[t("lab_tool.site.labels.slmin")]: {
			type: "number",
			allowDecimal: true,
		},
	};
	useEffect(() => {
		setFormValuesSite(valuesSite);
	}, [valuesSite]);

	const handleFormChange = (name, value) => {
		setFormValues({
			...formValues,
			[name]: value,
		});
		setFormErrors({
			...formErrors,
			[name]: false,
		});
	};

	const handleFieldChange = (fieldName, value) => {
		setFormValuesSite((prev) => ({
			...prev,
			[fieldName]: value,
		}));

		const errorMsg = validateFieldSite(fieldName, value, formValuesSite, t);

		setErrors((prevErrors) => ({
			...prevErrors,
			[fieldName]: errorMsg,
		}));
	};

	const handleContinue = () => {
		const errors = {
			name: formValues.name.trim() === "",
			description: formValues.description.trim() === "",
		};

		setFormErrors(errors);

		if (!errors.name && !errors.description) {
			setPaso(paso + 1); // Incrementa el paso solo si no hay errores
			setShowAlert(false); // Oculta la alerta si no hay errores
		} else {
			setShowAlert(true); // Muestra la alerta si hay errores
		}
	};

	const handleVolver = () => {
		setPaso(paso - 1);
	};

	const handleCloseModal = () => {
		setModalOpen(false);
	};

	const pasteYearMonth = (date) => {
		const [year, month] = date.split("-");
		return { year: parseInt(year, 10), month: parseInt(month, 10) };
	};

	const handleButtonClick = async () => {
		if (!fechaInicio || !fechaFin) {
			setFechaError(t("aux.messages.date_obligatory"));
			return;
		} else {
			const inicio = pasteYearMonth(fechaInicio);
			const fin = pasteYearMonth(fechaFin);

			const inicioTotal = inicio.year * 12 + inicio.month;
			const finTotal = fin.year * 12 + fin.month;
			if (inicioTotal > finTotal) {
				setFechaError(t("aux.messages.date_error"));
				return;
			} else {
				setFechaError("");
				setPaso(1);
			}
		}
	};
	const handleCheckboxChange = (event) => {
		const { name } = event.target;
		setChecked(name); // Actualizar el estado de checked primero
		if (name === "") {
			setCheckedError(t("aux.messages.select_checkbox"));
		} else {
			setCheckedError("");
		}
	};
	const handleClickCrearContrato = () => {
		setOpenModalCrear(true);
	};

	const handleDataCrearContrato = (data) => {
		setDatosContractArea((prevState) => [...prevState, data]);
	};

	const handleCrearContrato = () => {
		setOpenModalCrear(false);
	};

	const handleEditClick = (index) => {
		const contratoSeleccionado = datosContractArea[index];
		setSelectedContrato(contratoSeleccionado);
		setEditingIndex(index);
		setModalOpen(true);
	};

	const handleDataUpdate = (updateData) => {
		setDatosContractArea((prevData) => {
			let newData = [...prevData]; // Copia el array original
			newData[editingIndex] = updateData; // Reemplaza el elemento editado
			return newData;
		});
	};

	const eliminarContrato = (index) => {
		Swal.fire({
			title: t("aux.messages.sure"),
			text: t("aux.messages.not_revert"),
			icon: "warning",
			showCancelButton: true,
			confirmButtonColor: "#3085d6",
			cancelButtonColor: "#d33",
			confirmButtonText: t("aux.buttons.yes"),
		}).then((result) => {
			if (result.isConfirmed) {
				Swal.fire({
					title: t("aux.messages.alert.delete"),
					text: t("aux.messages.alert.text_alert.delete_contract"),
					icon: "success",
				}).then(() => {
					setDatosContractArea((prevData) => {
						const newData = [...prevData]; // Copia el array original
						newData.splice(index, 1); // Elimina el elemento en la posición index
						return newData;
					});
				});
			}
		});
	};

	const handleEjecutarSimulacion = async () => {
		// Verificar si el checkbox está seleccionado
		if (!checked) {
			setCheckedError(t("aux.messages.select_checkbox"));
			return;
		}
		setCheckedError("");

		// Configurar los datos a enviar al endpoint
		const datosEnpoint = {
			site_id: site_id,
			name: formValues.name,
			description: formValues.description,
			start: fechaInicio,
			end: fechaFin,
			send_mail: emailNotifications,
			config_tienda: {
				nombre: formValuesSite[t("lab_tool.site.labels.nombre")],
				opmin: formValuesSite[t("lab_tool.site.labels.opmin")],
				cu: formValuesSite[t("lab_tool.site.labels.cu")],
				co: formValuesSite[t("lab_tool.site.labels.co")],
				slmin: formValuesSite[t("lab_tool.site.labels.slmin")],
			},
			config: datosContractArea,
			forecast_origin: checked,
		};

		try {
			// Preguntar al usuario si desea continuar
			const confirmation = await Swal.fire({
				title: t("aux.messages.sure"),
				text: t("aux.messages.not_revert"),
				icon: "question",
				showCancelButton: true,
				confirmButtonColor: "#3085d6",
				cancelButtonColor: "#d33",
				confirmButtonText: t("aux.buttons.yes"),
			});

			if (confirmation.isConfirmed) {
				// Mostrar indicador de carga mientras se realiza la petición
				Swal.fire({
					title: t("aux.messages.alert.info"),
					text: t("aux.messages.alert.text_alert.wait_sim"),
					icon: "info",
					allowOutsideClick: false,
					showConfirmButton: false,
					didOpen: () => {
						Swal.showLoading();
					},
				});

				// Llamada al endpoint y espera su resolución
				await agregarDato("/launch", datosEnpoint);

				// Ejecuta auditoría una vez enviada la petición
				const moduleId = getModuleId(
					"Simulación",
					"Crear formulario de simulación (simulación desde 0)"
				);
				auditPost(moduleId, site_id);

				// Mostrar mensaje de éxito y redirigir
				await Swal.fire({
					title: t("aux.messages.alert.success"),
					text: t("aux.messages.alert.text_alert.success_sim"),
					icon: "success",
					showConfirmButton: false,
					timer: 1500,
				});
				navigate("/simulation");
			} else {
				// Opcional: Puedes manejar el caso en que el usuario cancele la acción
				Swal.fire({
					title: t("aux.messages.alert.cancel"),
					text: t("aux.messages.alert.text_alert.cancel_sim"),
					icon: "info",
				});
			}
		} catch (error) {
			console.error("Error al ejecutar la simulación:", error);
			Swal.fire({
				title: t("aux.messages.alert.error"),
				text: t("aux.messages.alert.text_alert.error_sim"),
				icon: "error",
			});
		}
	};

	return (
		<>
			{/* container principal */}
			<Box
				sx={{
					width: "100%",
					p: 2,
					display: "flex",
					flexDirection: "column",
					justifyContent: "center",
					alignItems: "center",
				}}
			>
				<Typography variant="h5" gutterBottom>
					{t("sim.sim_form.create_sim")}
				</Typography>
				{/* container Selects */}
				<Box
					sx={{
						p: 2,
						width: "75%",
					}}
				>
					<Grid container spacing={2}>
						<Grid item xs={6}>
							<TextField
								disabled={paso === 2 || paso === 3}
								id="dateInicio"
								label={t("aux.date_start")}
								type="month"
								value={fechaInicio}
								onChange={(e) => setFechaInicio(e.target.value)}
								InputLabelProps={{
									shrink: true,
								}}
								style={{ width: "100%" }}
								error={!!fechaError}
								helperText={fechaError && !fechaInicio ? fechaError : ""}
							/>
						</Grid>
						<Grid item xs={6}>
							<TextField
								disabled={paso === 2 || paso === 3}
								id="dateFin"
								label={t("aux.date_end")}
								type="month"
								value={fechaFin}
								onChange={(e) => setFechaFin(e.target.value)}
								error={!!fechaError}
								helperText={
									fechaError ? fechaError : fechaInicio > fechaFin ? t("aux.messages.date_error") : ""
								}
								InputLabelProps={{
									shrink: true,
								}}
								style={{ width: "100%" }}
							/>
						</Grid>

						<Grid item xs={12}>
							<Button
								disabled={paso === 2 || paso === 3}
								variant="contained"
								color="primary"
								size="large"
								sx={{ width: "100%", padding: "15px" }}
								onClick={handleButtonClick}
							>
								{t("aux.buttons.continue")}
							</Button>
						</Grid>
					</Grid>
				</Box>
				{/* Container Pasos */}
				<Box
					sx={{
						p: 2,
						width: "75%",
						display: "flex",
						flexDirection: "column",
						justifyContent: "center",
						alignItems: "center",
					}}
				>
					{paso === 1 && (
						<>
							<Box
								sx={{
									width: "100%",
									p: 2,
									marginBottom: 2,
									border: 1,
									borderColor: "secondary.main",
									borderRadius: 1,
								}}
							>
								<FormNameDesc
									formValues={formValues}
									formErrors={formErrors}
									onFormChange={handleFormChange}
								/>
							</Box>
							<Typography variant="h6" gutterBottom>
								{t("aux.step")} {paso} {t("aux.of")} 3 {t("sim.pop_up.conf_site")}
							</Typography>
							<Box
								sx={{
									width: "100%",
									p: 2,
									border: 1,
									borderColor: "secondary.main",
									borderRadius: 1,
								}}
							>
								<FormConfig
									labels={Object.keys(valuesSite)}
									handleFieldChange={handleFieldChange}
									valorDefault={formValuesSite}
									tooltips={tooltips}
									errors={errors}
									fieldConfigs={siteFieldConfigs}
								/>
								{showAlert && <Alert severity="error">{t("aux.messages.error_campos")}</Alert>}
							</Box>

							<Button variant="contained" onClick={handleContinue}>
								{t("aux.buttons.continue")}
							</Button>
						</>
					)}

					{paso === 2 && (
						<>
							<Typography variant="h6" gutterBottom>
								{t("aux.step")} {paso} {t("aux.of")} 3 {t("config.contract.title")}
							</Typography>
							<Box
								sx={{
									width: "100%",
									display: "flex",
									justifyContent: "end",
									px: 2,
								}}
							>
								<IconButton edge="end" aria-label="add" color="primary" onClick={handleClickCrearContrato}>
									<AddIcon
										sx={{
											fontSize: 40,
										}}
									/>
								</IconButton>
							</Box>
							{isLoading ? (
								<Box
									sx={{
										width: "100%",
										display: "flex",
										flexDirection: "column",
										justifyContent: "center",
										alignItems: "center",
									}}
								>
									<CircularProgress color="primary" />
								</Box>
							) : (
								showList && (
									<Box
										sx={{
											width: "100%",
											p: 2,
											border: 1,
											borderColor: "secondary.main",
											borderRadius: 1,
										}}
									>
										<List>
											{Object.keys(datosContractArea).map((key, index) => (
												<ListItem
													sx={{
														width: "100%",
														height: "8vh",
														border: 1,
														borderColor: "secondary.main",
														borderRadius: 1,
														my: 1,
													}}
													key={key}
												>
													<IconButton
														edge="start"
														aria-label="edit"
														color="primary"
														sx={{ p: 2 }}
														onClick={() => handleEditClick(key)}
													>
														<EditIcon />
													</IconButton>
													<ListItemText
														primary={`${datosContractArea[key].nombre}`}
														sx={{ p: 2, textAlign: "center" }}
													/>
													<IconButton
														edge="end"
														aria-label="delete"
														color="error"
														sx={{ p: 2 }}
														onClick={() => eliminarContrato(key)}
													>
														<DeleteIcon />
													</IconButton>
												</ListItem>
											))}
										</List>
									</Box>
								)
							)}
							<Button onClick={handleVolver}>{t("aux.buttons.back")}</Button>
							<Button variant="contained" onClick={handleContinue}>
								{t("aux.buttons.continue")}
							</Button>
						</>
					)}
				</Box>
				{paso === 3 && (
					<>
						<Typography variant="h6" gutterBottom>
							{t("aux.step")} {paso} {t("aux.of")} 3 {t("aux.origen_file")}
						</Typography>
						<FormControl
							required
							error={!!checkedError}
							component="fieldset"
							sx={{
								width: "100%",
								mb: 2,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							}}
							variant="standard"
						>
							<FormGroup>
								<FormLabel component="legend">{t("sim.sim_form.origen_demand")}</FormLabel>
								<FormControlLabel
									control={
										<Checkbox checked={checked === "KRONOS"} onChange={handleCheckboxChange} name="KRONOS" />
									}
									label="UKG"
								/>
								<FormControlLabel
									control={<Checkbox checked={checked === "DB"} onChange={handleCheckboxChange} name="DB" />}
									label={t("sim.sim_form.db")}
								/>
								{/* <div>
							Selected checkbox:{" "}
							{checked === "Kronos"
								? "Archivo desde Kronos"
								: checked === "DB"
								? "Archivo desde Base de datos"
								: "None"}
						</div> */}
							</FormGroup>
							{checkedError && <FormHelperText>{t("aux.messages.select_checkbox")}</FormHelperText>}
						</FormControl>
						<FormControlLabel
							control={
								<Switch
									checked={emailNotifications}
									onChange={(e) => setEmailNotifications(e.target.checked)}
								/>
							}
							label={t("sim.sim_form.email_notify")}
						/>
						<Button onClick={handleVolver}>{t("aux.buttons.back")}</Button>
						<Button variant="contained" onClick={() => handleEjecutarSimulacion()}>
							{t("aux.buttons.end")}
						</Button>
					</>
				)}
			</Box>
			<ModalEdit
				open={modalOpen}
				onClose={handleCloseModal}
				contractData={selectedContrato}
				handleDataUpdate={handleDataUpdate}
			/>
			<ModalCreateContract
				open={openModalCrear}
				onClose={handleCrearContrato}
				handleDataCrearContrato={handleDataCrearContrato}
			/>
		</>
	);
};

export { FormSimulation };
